import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { companyAdminRecipesAPI } from "../../lib/companyAdminApi";

const parseIngredients = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const RecipeFormModal = ({ open, onClose, recipe, onSaved }) => {
  const isEdit = Boolean(recipe);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [servings, setServings] = useState(1);
  const [ingredientsText, setIngredientsText] = useState("");
  const [instructions, setInstructions] = useState("");
  const [servingSuggestion, setServingSuggestion] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(recipe?.name || "");
    setNotes(recipe?.notes || "");
    setServings(recipe?.servings || 1);
    setIngredientsText((recipe?.ingredients || []).join("\n"));
    setInstructions(recipe?.instructions || "");
    setServingSuggestion(recipe?.serving_suggestion || "");
  }, [open, recipe]);

  const handleSave = async () => {
    const trimmedName = String(name || "").trim();
    const ingredients = parseIngredients(ingredientsText);
    if (!trimmedName) return toast.error("Please enter a recipe name");
    if (ingredients.length === 0) {
      return toast.error("Add ingredients (one per line with quantities)");
    }
    setSaving(true);
    try {
      const payload = {
        name: trimmedName,
        notes: String(notes || "").trim() || null,
        servings: Number(servings) > 0 ? Number(servings) : 1,
        ingredients,
        instructions: String(instructions || "").trim() || null,
        serving_suggestion: String(servingSuggestion || "").trim() || null,
      };
      if (isEdit) {
        await companyAdminRecipesAPI.update(recipe._id, payload);
        toast.success("Recipe updated");
      } else {
        await companyAdminRecipesAPI.create(payload);
        toast.success("Recipe saved");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save recipe");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="panel w-full max-w-xl mx-4 p-0 max-h-[85vh] app-modal-shell flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="recipe-form-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            {isEdit ? "Edit Recipe" : "Add Recipe"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <div className="app-modal-body p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
            Save a recipe once, then reuse it from "Add Food by Recipe" or "Add
            Food by AI" for any customer — no more retyping ingredients each
            time.
          </div>

          <div>
            <Label className="text-foreground">Recipe name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Grilled chicken with quinoa salad"
              disabled={saving}
            />
          </div>

          <div>
            <Label className="text-foreground">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. low oil, Pakistani style, no dairy"
              disabled={saving}
              data-testid="recipe-notes-input"
            />
          </div>

          <div>
            <Label className="text-foreground">Servings</Label>
            <Input
              type="number"
              min="1"
              max="12"
              step="1"
              value={servings}
              onChange={(e) =>
                setServings(e.target.value === "" ? "" : Number(e.target.value))
              }
              disabled={saving}
            />
          </div>

          <div>
            <Label className="text-foreground">
              Ingredients (one per line, with amounts)
            </Label>
            <Textarea
              value={ingredientsText}
              onChange={(e) => setIngredientsText(e.target.value)}
              placeholder={
                "200 g chicken breast\n1 cup cooked quinoa\n1 tbsp olive oil"
              }
              rows={5}
              disabled={saving}
              className="resize-y min-h-[100px] font-mono text-sm"
            />
          </div>

          <div>
            <Label className="text-foreground">
              One serving looks like (optional)
            </Label>
            <Input
              value={servingSuggestion}
              onChange={(e) => setServingSuggestion(e.target.value)}
              placeholder="e.g. ~320 g plated portion"
              disabled={saving}
            />
          </div>

          <div>
            <Label className="text-foreground">Instructions (optional)</Label>
            <Textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Brief prep / cook method"
              rows={2}
              disabled={saving}
              className="resize-y min-h-[60px]"
            />
          </div>
        </div>

        <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              saving ||
              !String(name || "").trim() ||
              parseIngredients(ingredientsText).length === 0
            }
            data-testid="recipe-save"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Save Recipe"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const RecipesPage = () => {
  const [loading, setLoading] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAdminRecipesAPI.getAll({
        page,
        limit: 20,
        search: search.trim() || undefined,
      });
      setRecipes(res.data.recipes || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch {
      toast.error("Failed to load recipes");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  const openCreate = () => {
    setEditingRecipe(null);
    setFormOpen(true);
  };

  const openEdit = (recipe) => {
    setEditingRecipe(recipe);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await companyAdminRecipesAPI.delete(deleteTarget._id);
      toast.success("Recipe deleted");
      setDeleteTarget(null);
      loadRecipes();
    } catch {
      toast.error("Failed to delete recipe");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-primary" />
              Recipes
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Reusable recipes for "Add Food by Recipe" and "Add Food by AI" —
              save once, use for any customer.
            </p>
          </div>
          <Button className="gap-2" onClick={openCreate} data-testid="add-recipe">
            <Plus className="w-4 h-4" />
            Add Recipe
          </Button>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search recipes..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>

        <div className="panel border border-border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : recipes.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              No recipes yet. Add one to reuse across customers.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Servings</TableHead>
                  <TableHead>Ingredients</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipes.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell className="font-medium text-foreground">
                      {r.name}
                    </TableCell>
                    <TableCell>{r.servings || 1}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate">
                      {(r.ingredients || []).join(", ") || "—"}
                    </TableCell>
                    <TableCell className="capitalize">
                      {r.source || "manual"}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDate(r.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(r)}
                        title="Edit recipe"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(r)}
                        title="Delete recipe"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page} of {pages} ({total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <RecipeFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        recipe={editingRecipe}
        onSaved={loadRecipes}
      />

      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => !deleting && setDeleteTarget(null)}
          role="presentation"
        >
          <div
            className="panel w-full max-w-sm mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-foreground">
              Delete recipe?
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              "{deleteTarget.name}" will be permanently removed. This won't
              affect foods already created from it.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </CompanyAdminLayout>
  );
};

export default RecipesPage;
